import { useEffect, useRef, useState } from 'react';

import { Modal, dialogService, toastService } from '@thefarmersfront/kitchen';
import { isDeepEqual } from '@thefarmersfront/kitchen-utensil';

import { useShallow } from 'zustand/react/shallow';

import { useGetExchangeOrdersExistence, usePostExchangeOrders } from '~/hooks/claim';
import { useGetOrderOutboundOrderCode } from '~/hooks/order';
import { initialClaimStoreValue, useClaimStore } from '~/store/claim';
import { addIf, focusFirstErrorField, formatTel, transformToSelectOption, validateTelFormat } from '~/utils';

import { CLAIM_TOTAL_BOX_COUNT } from '~/constants/claim';
import {
  DEFAULT_INPUT_STATUS,
  ERROR_INVALID_FORMAT_INPUT_STATUS,
  ERROR_REQUIRED_INPUT_STATUS,
  ERROR_REQUIRED_SELECT_STATUS,
  ERROR_STATUS,
  TOAST_MESSAGE,
} from '~/constants/common';

import { ClaimRegisterFirstStepContent } from '../../ClaimRegisterFirstStepContent';
import ClaimExchangeRegisterSecondStepContent from './ClaimExchangeRegisterSecondStepContent/ClaimExchangeRegisterSecondStepContent';

import type {
  TypeExchangeRegisterSecondStepErrorFieldRefs,
  TypeExchangeRegisterSecondStepValues,
  TypeExchangeRegisterSecondStepValuesState,
  TypePostExchangeOrdersParams,
} from '~/types/claim.types';
import type { TypeOrders } from '~/types/order.types';

const DEFAULT_ERROR_FIELD_REFS: TypeExchangeRegisterSecondStepErrorFieldRefs = {
  'returnInfo.totalBox': null,
  'returnInfo.totalBoxInput': null,
  'returnInfo.receiverContactNumber': null,
  'returnInfo.primaryAddress': null,
  'returnInfo.returnPlace': null,
  'returnInfo.accessMethod': null,
  'exchangeDeliveryInfo.receiverContactNumber': null,
  'exchangeDeliveryInfo.primaryAddress': null,
  'exchangeDeliveryInfo.receivePlace': null,
  'exchangeDeliveryInfo.accessMethod': null,
};

const ERROR_FIELD_FOCUS_ORDER: (keyof TypeExchangeRegisterSecondStepErrorFieldRefs)[] = [
  'exchangeDeliveryInfo.receiverContactNumber',
  'exchangeDeliveryInfo.primaryAddress',
  'exchangeDeliveryInfo.receivePlace',
  'returnInfo.totalBox',
  'returnInfo.totalBoxInput',
  'exchangeDeliveryInfo.accessMethod',
  'returnInfo.receiverContactNumber',
  'returnInfo.primaryAddress',
  'returnInfo.returnPlace',
  'returnInfo.accessMethod',
];

const ClaimExchangeRegisterModal = ({
  isOpen,
  onCancel,
  onConfirm,
}: {
  isOpen: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) => {
  const errorFieldRefs = useRef<TypeExchangeRegisterSecondStepErrorFieldRefs>(DEFAULT_ERROR_FIELD_REFS);

  const { requestExchangeOrdersExistence } = useGetExchangeOrdersExistence();
  const { requestOrderDetail } = useGetOrderOutboundOrderCode();
  const { registerExchangeOrders } = usePostExchangeOrders();

  const [
    /** step 1 */
    firstStepSearchFilterValues,
    setFirstStepSearchFilterValues,
    /** step 2 */
    secondStepExchangeInfoValues,
    secondStepExchangeInfoValuesState,
    setSecondStepExchangeInfoValues,
    setSecondStepExchangeInfoValuesState,
    /** 공통 */
    selectOptions,
    resetClaimStore,
  ] = useClaimStore(
    useShallow((state) => [
      state.registerFirstStepSearchFilterValues,
      state.actions.setRegisterFirstStepSearchFilterValues,
      state.exchangeRegisterSecondStepValues,
      state.exchangeRegisterSecondStepValuesState,
      state.actions.setExchangeRegisterSecondStepValues,
      state.actions.setExchangeRegisterSecondStepValuesState,
      state.selectOptions,
      state.actions.resetStore,
    ])
  );

  const defaultExchangeInfoValues = initialClaimStoreValue.exchangeRegisterSecondStepValues;
  const defaultExchangeInfoValuesState = initialClaimStoreValue.exchangeRegisterSecondStepValuesState;

  const [currentStep, setCurrentStep] = useState<1 | 2>(1);
  const [selectedOrder, setSelectedOrder] = useState<TypeOrders | null>(null);
  const [initialExchangeInfoValues, setInitialExchangeInfoValues] =
    useState<TypeExchangeRegisterSecondStepValues>(defaultExchangeInfoValues);

  useEffect(() => {
    setFirstStepSearchFilterValues({
      ...firstStepSearchFilterValues,
      keywordType: selectOptions.orderKeyword[0] ?? null,
      dateType: selectOptions.orderDateType[0] ?? null,
    });

    return () =>
      resetClaimStore([
        'registerFirstStepSearchFilterValues',
        'registerFirstStepSearchFilterValuesStatus',
        'registerFirstStepOrdersData',
        'exchangeRegisterSecondStepValues',
        'exchangeRegisterSecondStepValuesState',
      ]);
  }, []);

  const handleCancel = () => {
    const isModified = !isDeepEqual(initialExchangeInfoValues, secondStepExchangeInfoValues);

    if (isModified) {
      dialogService.addDialog({
        type: 'confirm',
        children: '작성된 내용이 저장되지 않습니다.\n페이지를 나가시겠습니까?',
        confirmText: '나가기',
        onConfirm: onCancel,
      });
      return;
    }

    onCancel();
  };

  const validateExchangeInfoValues = () => {
    const { returnInfo, exchangeDeliveryInfo, exchangeOrderItems } = secondStepExchangeInfoValues;

    const updatedState: TypeExchangeRegisterSecondStepValuesState = {
      ...secondStepExchangeInfoValuesState,
      returnInfo: {
        ...secondStepExchangeInfoValuesState.returnInfo,
        ...addIf(!returnInfo.totalBox, 'totalBox', ERROR_REQUIRED_SELECT_STATUS),
        ...addIf(
          returnInfo.totalBox?.value === 'ETC' && !returnInfo.totalBoxInput,
          'totalBoxInput',
          ERROR_REQUIRED_INPUT_STATUS
        ),
        ...addIf(
          returnInfo.totalBox?.value === 'ETC' &&
            returnInfo.totalBoxInput.trim() !== '' &&
            Number(returnInfo.totalBoxInput) < CLAIM_TOTAL_BOX_COUNT.MIN,
          'totalBoxInput',
          {
            status: 'error',
            message: `${CLAIM_TOTAL_BOX_COUNT.MIN} 이상으로 입력해주세요.`,
          }
        ),
        ...addIf(
          returnInfo.totalBox?.value === 'ETC' &&
            returnInfo.totalBoxInput.trim() !== '' &&
            Number(returnInfo.totalBoxInput) > CLAIM_TOTAL_BOX_COUNT.MAX,
          'totalBoxInput',
          {
            status: 'error',
            message: `${CLAIM_TOTAL_BOX_COUNT.MAX} 이하로 입력해주세요.`,
          }
        ),
        ...addIf(!returnInfo.receiverContactNumber, 'receiverContactNumber', ERROR_REQUIRED_INPUT_STATUS),
        ...addIf(
          returnInfo.receiverContactNumber && !validateTelFormat(returnInfo.receiverContactNumber),
          'receiverContactNumber',
          ERROR_INVALID_FORMAT_INPUT_STATUS
        ),
        ...addIf(!returnInfo.primaryAddress, 'primaryAddress', ERROR_REQUIRED_INPUT_STATUS),
        ...addIf(!returnInfo.returnPlace, 'returnPlace', ERROR_REQUIRED_SELECT_STATUS),
        ...addIf(!returnInfo.accessMethod, 'accessMethod', ERROR_REQUIRED_SELECT_STATUS),
      },
      exchangeDeliveryInfo: {
        ...secondStepExchangeInfoValuesState.exchangeDeliveryInfo,
        ...addIf(!exchangeDeliveryInfo.receiverContactNumber, 'receiverContactNumber', ERROR_REQUIRED_INPUT_STATUS),
        ...addIf(
          exchangeDeliveryInfo.receiverContactNumber && !validateTelFormat(exchangeDeliveryInfo.receiverContactNumber),
          'receiverContactNumber',
          ERROR_INVALID_FORMAT_INPUT_STATUS
        ),
        ...addIf(!exchangeDeliveryInfo.primaryAddress, 'primaryAddress', ERROR_REQUIRED_INPUT_STATUS),
        ...addIf(!exchangeDeliveryInfo.receivePlace, 'receivePlace', ERROR_REQUIRED_SELECT_STATUS),
        ...addIf(!exchangeDeliveryInfo.accessMethod, 'accessMethod', ERROR_REQUIRED_SELECT_STATUS),
      },
      exchangeOrderItems: exchangeOrderItems.map((item) => ({
        itemId: item.itemId,
        exchangeQuantity: item.isExchange && !item.exchangeQuantity ? ERROR_STATUS : DEFAULT_INPUT_STATUS,
        exchangeReason: item.isExchange && !item.exchangeReason ? ERROR_STATUS : DEFAULT_INPUT_STATUS,
        exchangeDeliveryItems: item.exchangeDeliveryItems.map((item) => ({
          itemId: item.itemId,
          quantity: item.quantity ? DEFAULT_INPUT_STATUS : ERROR_STATUS,
        })),
      })),
    };

    setSecondStepExchangeInfoValuesState(updatedState);

    /** 교환 상품 리스트 > 반품여부 전체 미선택 시 */
    if (secondStepExchangeInfoValues.exchangeOrderItems.every((item) => !item.isExchange)) {
      dialogService.addDialog({
        children: '교환 신청할 상품을 1개 이상 체크해주세요.',
      });
      return false;
    }

    /** 교환 상품 리스트 > 교환 배송 상품 미등록 시 */
    if (
      secondStepExchangeInfoValues.exchangeOrderItems.some(
        (item) => item.isExchange && item.exchangeDeliveryItems.length === 0
      )
    ) {
      dialogService.addDialog({
        children:
          '교환배송할 상품을 1개 이상 추가해주세요.\n동일한 상품을 교환배송한다면 동일상품 교환여부를 체크해주세요.',
      });
      return false;
    }

    /** 교환 상품 리스트 > 필수값 미입력/미선택 시 */
    if (
      updatedState.exchangeOrderItems.some(
        (item) =>
          item.exchangeQuantity.status === 'error' ||
          item.exchangeReason.status === 'error' ||
          item.exchangeDeliveryItems.some((item) => item.quantity.status === 'error')
      )
    ) {
      dialogService.addDialog({
        children: '미입력한 교환 상품 정보가 있습니다.\n다시 확인 후 시도해주세요.',
      });
      return false;
    }

    const isValid =
      Object.values(updatedState.returnInfo).every((item) => item.status !== 'error') &&
      Object.values(updatedState.exchangeDeliveryInfo).every((item) => item.status !== 'error');

    if (!isValid) {
      // returnInfo와 exchangeDeliveryInfo 상태를 하나의 객체로 합치기 위해 키에 prefix 추가
      const flattenedStates = {
        ...Object.fromEntries(
          Object.entries(updatedState.returnInfo).map(([key, value]) => [`returnInfo.${key}`, value])
        ),
        ...Object.fromEntries(
          Object.entries(updatedState.exchangeDeliveryInfo).map(([key, value]) => [
            `exchangeDeliveryInfo.${key}`,
            value,
          ])
        ),
      };
      focusFirstErrorField(errorFieldRefs.current, flattenedStates, ERROR_FIELD_FOCUS_ORDER);
    }

    return isValid;
  };

  const handleConfirm = () => {
    const isValid = validateExchangeInfoValues();

    if (!isValid) return;

    dialogService.addDialog({
      type: 'confirm',
      children: '등록 시 즉시 교환 처리가 시작됩니다.\n등록하시겠습니까?',
      confirmText: '등록',
      onConfirm: registerExchange,
    });
  };

  const registerExchange = () => {
    const { returnInfo, exchangeDeliveryInfo, exchangeOrderItems } = secondStepExchangeInfoValues;

    const params: TypePostExchangeOrdersParams = {
      exchangeType: returnInfo.exchangeType,
      totalBox:
        returnInfo.totalBox?.value === 'ETC'
          ? Number(returnInfo.totalBoxInput)
          : Number(returnInfo.totalBox?.value ?? 0),
      returnMember: {
        name: returnInfo.receiverName,
        phoneNumber: returnInfo.receiverContactNumber.replaceAll('-', ''),
      },
      returnPlace: {
        primaryAddress: returnInfo.primaryAddress,
        secondaryAddress: returnInfo.detailAddress,
        accessMethod: returnInfo.accessMethod?.value ?? '',
        accessDetail: returnInfo.accessDetail,
        pickupType: returnInfo.returnPlace?.value ?? '',
        pickupDetail: returnInfo.returnPlaceDetail,
        memo: returnInfo.courierMemo,
      },
      exchangeMember: {
        name: exchangeDeliveryInfo.receiverName,
        phoneNumber: exchangeDeliveryInfo.receiverContactNumber.replaceAll('-', ''),
      },
      exchangePlace: {
        primaryAddress: exchangeDeliveryInfo.primaryAddress,
        secondaryAddress: exchangeDeliveryInfo.detailAddress,
        accessMethod: exchangeDeliveryInfo.accessMethod?.value ?? '',
        accessDetail: exchangeDeliveryInfo.accessDetail,
        pickupType: exchangeDeliveryInfo.receivePlace?.value ?? '',
        pickupDetail: exchangeDeliveryInfo.receivePlaceDetail,
      },
      exchangeReturnOrderItems: exchangeOrderItems
        .filter((item) => item.isExchange)
        .map((item) => ({
          goodsCode: item.itemId,
          quantity: Number(item.exchangeQuantity),
          reasonCode: item.exchangeReason?.value ?? '',
          exchangeItems: item.exchangeDeliveryItems.map((item) => ({
            goodsCode: item.itemId,
            quantity: Number(item.quantity),
          })),
        })),
    };

    registerExchangeOrders(
      { params, pathParams: { outboundOrderCode: returnInfo.outboundCode } },
      {
        onSuccess: onConfirm,
        onError: (error) => {
          toastService.addToast({
            type: 'error',
            message: error?.message || TOAST_MESSAGE.DEFAULT_ERROR,
          });
        },
      }
    );
  };
  const moveToStep2 = () => {
    if (!selectedOrder) return;

    requestOrderDetail(
      { outboundOrderCode: selectedOrder.outbound.outboundCode },
      {
        onSuccess: (data) => {
          const initialExchangeInfoValues: TypeExchangeRegisterSecondStepValues = {
            ...secondStepExchangeInfoValues,
            returnInfo: {
              ...secondStepExchangeInfoValues.returnInfo,
              outboundCode: data.outbound.outboundCode,
              outboundClusterCenter: data.outbound.clusterCenter.name,
              outboundCourier: data.delivery.courier.name,
              receiverName: data.receiver.name,
              receiverContactNumber: formatTel(data.receiver.phoneNumber),
              primaryAddress: data.receiver.address.primaryAddress,
              detailAddress: data.receiver.address.secondAddress,
              returnPlace: selectOptions.returnPlace[0],
              accessMethod: selectOptions.accessMethod[0],
            },
            exchangeDeliveryInfo: {
              ...secondStepExchangeInfoValues.exchangeDeliveryInfo,
              exchangeCourier: data.delivery.courier.name,
              receiverName: data.receiver.name,
              receiverContactNumber: formatTel(data.receiver.phoneNumber),
              primaryAddress: data.receiver.address.primaryAddress,
              detailAddress: data.receiver.address.secondAddress,
              receivePlace:
                transformToSelectOption(data.receiver.deliveryRequest?.pickupPlaceType, 'code', 'name') ??
                selectOptions.returnPlace[0],
              receivePlaceDetail: data.receiver.deliveryRequest.pickupDetail,
              accessMethod:
                transformToSelectOption(data.receiver.deliveryRequest.accessMethod, 'code', 'name') ??
                selectOptions.accessMethod[0],
              accessDetail: data.receiver.deliveryRequest.accessDetail,
            },
            exchangeOrderItems: data.order.orderItems.map((item) => ({
              ...item,
              isExchange: false,
              exchangeQuantity: '',
              exchangeReason: null,
              isSameGoods: false,
              exchangeDeliveryItems: [],
            })),
          };

          setSecondStepExchangeInfoValues(initialExchangeInfoValues);
          setInitialExchangeInfoValues(initialExchangeInfoValues);

          setSecondStepExchangeInfoValuesState({
            ...secondStepExchangeInfoValuesState,
            exchangeOrderItems: data.order.orderItems.map((item) => ({
              itemId: item.itemId,
              exchangeQuantity: DEFAULT_INPUT_STATUS,
              exchangeReason: DEFAULT_INPUT_STATUS,
              exchangeDeliveryItems: [],
            })),
          });
          setCurrentStep(2);
        },
        onError: (error) =>
          toastService.addToast({
            type: 'error',
            message: error?.message || TOAST_MESSAGE.DEFAULT_ERROR,
          }),
      }
    );
  };

  const handleStepChange = (step: number) => {
    if (step === 1) {
      const isModified = !isDeepEqual(initialExchangeInfoValues, secondStepExchangeInfoValues);

      if (isModified) {
        dialogService.addDialog({
          type: 'confirm',
          children: '작성된 내용이 저장되지 않습니다.\n이전 페이지로 이동하시겠습니까?',
          confirmText: '이전으로',
          onConfirm: () => {
            setSelectedOrder(null);
            setSecondStepExchangeInfoValues(defaultExchangeInfoValues);
            setInitialExchangeInfoValues(defaultExchangeInfoValues);
            setSecondStepExchangeInfoValuesState(defaultExchangeInfoValuesState);
            setCurrentStep(1);
          },
        });
        return;
      }

      setCurrentStep(1);
    } else if (step === 2) {
      if (!selectedOrder) {
        dialogService.addDialog({
          children: '교환 신청할 주문을 선택해주세요.',
        });
        return;
      }

      requestExchangeOrdersExistence(
        { outboundOrderCode: selectedOrder.outbound.outboundCode },
        {
          onSuccess: (isExchangeExist) => {
            if (isExchangeExist) {
              dialogService.addDialog({
                type: 'confirm',
                children: '이미 반품 또는 교환을 신청한 주문입니다.\n계속 신청하시겠습니까?',
                confirmText: '계속 신청',
                onConfirm: moveToStep2,
              });
              return;
            }

            moveToStep2();
          },
          onError: (error) => {
            toastService.addToast({
              type: 'error',
              message: error?.message || TOAST_MESSAGE.DEFAULT_ERROR,
            });
          },
        }
      );
    }
  };

  return (
    <Modal
      title="교환 신청 등록"
      size="xlarge"
      open={isOpen}
      onCancel={handleCancel}
      onConfirm={handleConfirm}
      confirmText="등록"
      step={{
        steps: [{ title: '교환 주문 선택' }, { title: '교환 정보 입력' }],
        currentStep,
        onStepChange: handleStepChange,
      }}
    >
      {currentStep === 1 && <ClaimRegisterFirstStepContent mode="exchange" setSelectedItem={setSelectedOrder} />}
      {currentStep === 2 && <ClaimExchangeRegisterSecondStepContent errorFieldRefs={errorFieldRefs} />}
    </Modal>
  );
};

export default ClaimExchangeRegisterModal;
